import type { ButtonVariant } from "@qendo/theme";
import { type ModalProps, type NativeSyntheticEvent, StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Button } from "~/components/Button";
import { PartialModal } from "~/components/layout/modal/PartialModal";
import { BodyText, Title } from "~/components/layout/text";
import { Radius, Space } from "~/constants/sizing";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";

export type ConfirmationModalProps = {
    onConfirm: ((event: NativeSyntheticEvent<unknown>) => void) | (() => void);
    title?: string;
    description?: string;
    cancelText?: string;
    confirmText?: string;
    confirmVariant?: ButtonVariant;
    loading?: boolean;
} & ModalProps;

export const ConfirmationModal = ({
    onConfirm,
    title,
    description,
    cancelText = "Cancel",
    confirmText = "Confirm",
    confirmVariant = "quiet",
    loading,
    children,
    ...modalProps
}: ConfirmationModalProps) => {
    const insets = useSafeAreaInsets();
    const { background, colours, border } = useTheme();

    return (
        <PartialModal {...modalProps} style={[styles.container, modalProps.style]}>
            <View style={[styles.container, { paddingBottom: insets.bottom + Space.md }]}>
                <View style={[styles.body, background.tertiary]}>
                    {filled(title || description) && (
                        <View style={[styles.header, border.quaternary]}>
                            {title && (
                                <Title size="md" colour="secondary">
                                    {title}
                                </Title>
                            )}
                            {description && (
                                <BodyText size="md" colour="secondary">
                                    {description}
                                </BodyText>
                            )}
                        </View>
                    )}
                    {filled(children) && (
                        <View style={{ borderBottomWidth: 1, borderBottomColor: colours.border.quaternary }}>
                            {children}
                        </View>
                    )}
                    <Button
                        variant={confirmVariant}
                        title={confirmText}
                        size="lg"
                        onPress={onConfirm}
                        disabled={loading}
                        loading={loading}
                    />
                </View>
                <View style={[styles.body, background.secondary]}>
                    <Button
                        variant="quiet"
                        title={cancelText}
                        size="lg"
                        onPress={modalProps.onRequestClose}
                        disabled={loading}
                    />
                </View>
            </View>
        </PartialModal>
    );
};

const styles = StyleSheet.create({
    container: {
        justifyContent: "flex-end",
        alignItems: "stretch",
        gap: Space.xs,
        flex: 1,
    },
    body: {
        width: "100%",
        borderRadius: Radius.sm,
        alignItems: "stretch",
        alignSelf: "center",
        maxWidth: "100%",
    },
    header: {
        gap: Space.xxx,
        padding: Space.md,
        borderWidth: 0,
        borderBottomWidth: 1,
    },
});
